import React, { useEffect, useState } from "react";
import { Constants } from "../constants";

// Fonction pour détecter iOS
const isIOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad sur iOS 13
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

// Fonction pour détecter si l'app est installée
const isAppInstalled = () => {
  // Vérifier le mode standalone
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }

  // Vérifier pour iOS
  if (window.navigator.standalone === true) {
    return true;
  }

  // Vérifier si l'app a été installée précédemment
  if (localStorage.getItem("bringme-installed") === "true") {
    return true;
  }

  return false;
};

// Fonction pour détecter le navigateur
const getBrowser = () => {
  // Si c'est iOS, on retourne toujours 'safari' car c'est le seul qui compte sur iOS
  if (isIOS()) {
    return "safari";
  }

  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("chrome") && !userAgent.includes("edg")) {
    return "chrome";
  } else if (userAgent.includes("firefox")) {
    return "firefox";
  } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
    return "safari";
  } else if (userAgent.includes("edg")) {
    return "edge";
  }
  return "other";
};

// Instructions d'installation par navigateur
const installInstructions = {
  firefox:
    "Pour installer Bring Me sur Firefox :\n1. Cliquez sur le menu (≡)\n2. Cliquez sur 'Installer Bring Me'",
  safari:
    "Pour installer Bring Me sur iOS :\n1. Cliquez sur le bouton de partage (⎋)\n2. Faites défiler et cliquez sur 'Sur l'écran d'accueil'",
  edge: "Pour installer Bring Me sur Edge :\n1. Cliquez sur le menu (⋯)\n2. Cliquez sur 'Applications'\n3. Cliquez sur 'Installer Bring Me'",
  other:
    "Pour installer Bring Me, utilisez le menu de votre navigateur pour ajouter cette page à votre écran d'accueil.",
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [browser, setBrowser] = useState(getBrowser());
  const [isPWAInstalled, setIsPWAInstalled] = useState(isAppInstalled());

  useEffect(() => {
    // Si l'app est déjà installée, ne rien faire
    if (isPWAInstalled) {
      return;
    }

    // Sur iOS, on montre toujours les instructions Safari
    if (isIOS()) {
      setShowInstallButton(true);
      return;
    }

    // Gérer le prompt d'installation pour Chrome/Edge (non-iOS)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Écouter l'événement d'installation réussie
    const handleAppInstalled = () => {
      setIsPWAInstalled(true);
      setShowInstallButton(false);
      // Marquer l'app comme installée dans le localStorage
      localStorage.setItem("bringme-installed", "true");
    };

    // Vérifier périodiquement si l'app est en mode standalone
    const checkStandalone = () => {
      if (isAppInstalled()) {
        setIsPWAInstalled(true);
        setShowInstallButton(false);
        localStorage.setItem("bringme-installed", "true");
      }
    };

    // Vérifier toutes les 2 secondes pendant les 10 premières secondes
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkStandalone();
      checkCount++;
      if (checkCount >= 5) clearInterval(checkInterval);
    }, 2000);

    if (browser === "chrome" || browser === "edge") {
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);
    } else {
      setShowInstallButton(true);
    }

    return () => {
      clearInterval(checkInterval);
      if (browser === "chrome" || browser === "edge") {
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt
        );
        window.removeEventListener("appinstalled", handleAppInstalled);
      }
    };
  }, [browser, isPWAInstalled]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      if (outcome === "accepted") {
        localStorage.setItem("bringme-installed", "true");
      }
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const handleClose = () => {
    setShowInstallButton(false);
    // Si l'utilisateur ferme le popup, on le marque comme vu
    localStorage.setItem("bringme-install-prompt-closed", "true");
  };

  // Ne pas afficher si l'app est installée ou si l'utilisateur a fermé le popup
  if (
    isPWAInstalled ||
    !showInstallButton ||
    localStorage.getItem("bringme-install-prompt-closed") === "true"
  ) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        background: Constants.colors.white,
        padding: "16px",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        maxWidth: "90%",
        width: "400px",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h3 style={{ margin: 0, color: Constants.colors.main }}>
          Installer Bring Me
        </h3>
        <p
          style={{
            margin: "8px 0 0",
            color: Constants.colors.grey,
            whiteSpace: "pre-line",
          }}
        >
          {(browser === "chrome" || browser === "edge") && !isIOS()
            ? "Installez notre application pour une meilleure expérience"
            : installInstructions[browser]}
        </p>
      </div>
      <div style={{ display: "flex", gap: "8px", width: "100%" }}>
        <button
          onClick={handleClose}
          style={{
            flex: 1,
            padding: "8px 16px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            background: "white",
            cursor: "pointer",
          }}
        >
          Plus tard
        </button>
        {(browser === "chrome" || browser === "edge") && !isIOS() && (
          <button
            onClick={handleInstallClick}
            style={{
              flex: 1,
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              background: Constants.colors.main,
              color: "white",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Installer
          </button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
